/**
 * PolicyAuctionModal.tsx — T-2 Policy Auction minigame
 *
 * Triggered every 3rd policy-tile landing per seat (auctionLandingCount[seat] % 3 === 0).
 * Replaces the normal bureaucrat card draw.
 *
 * Flow:
 *   1. Privacy beat — "look away" overlay for ~3s then auto-dismisses to show bidding.
 *   2. Active bidder (activeBidder) sees 1 face-up policy card (DD Bug#9a fix), token slider,
 *      Confirm Bid / Skip buttons.
 *   3. After submitting/skipping: "Pass Device to P{N}" button appears (passAuctionTurn advances
 *      activeBidder — DD Bug#9b fix). Jailed seats are shown a jail message and auto-skipped
 *      (DD Bug#9c fix).
 *   4. After all 4 have bid (or 30s total timer expires) → reveal.
 *   5. Reveal: show all bids, highlight winner, debit tokens, grant card.
 *      If winner's hand is full → triggers HandFullModal flow.
 *
 * INVARIANTS:
 *   - Returns null when boardAnimationActive === true (R11.2 gate).
 *   - Returns null when pendingAuction === null.
 *   - No global aura side-effects — NO bySeat[-1].
 *   - Card art / data NEVER modified (IMMUTABLE rule).
 *
 * VISIBLE WHEN: pendingAuction !== null AND boardAnimationActive === false
 * TEST PATH: land on Policy tile × 3 → auction triggers on 3rd landing.
 */

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "../../i18n/useI18n.js";
import { SEAT_COLORS, type SeatIndex, getCardById, useGameStore } from "../../store/game-store.js";
import styles from "./PolicyAuctionModal.module.css";

const PRIVACY_BEAT_MS = 3_000; // 3s look-away overlay before bidder sees controls

function seatLabel(seat: SeatIndex): string {
  return `P${seat + 1}`;
}

export function PolicyAuctionModal(): React.ReactElement | null {
  const { t } = useI18n();
  const pendingAuction = useGameStore((s) => s.pendingAuction);
  const numPlayers = useGameStore((s) => s.numPlayers);
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  // R13 Bug #14: currentSeat removed — bidding now keyed on activeBidder (not the drawer's seat).
  const tokens = useGameStore((s) => s.tokens);
  const jailTurnsRemaining = useGameStore((s) => s.jailTurnsRemaining);
  const submitAuctionBid = useGameStore((s) => s.submitAuctionBid);
  const skipAuctionBid = useGameStore((s) => s.skipAuctionBid);
  const passAuctionTurn = useGameStore((s) => s.passAuctionTurn);
  const revealAuction = useGameStore((s) => s.revealAuction);
  const closeAuction = useGameStore((s) => s.closeAuction);

  // Privacy beat: show "look away" for 3s on activeBidder change
  const [privacyVisible, setPrivacyVisible] = useState(true);
  const lastActiveBidderRef = useRef<number>(-1);

  // Countdown display
  const [secondsLeft, setSecondsLeft] = useState(30);
  const resolvedRef = useRef(false);

  // Local slider state — reset when activeBidder changes or privacy ends
  const [bidValue, setBidValue] = useState(0);

  // Reset privacy beat whenever activeBidder changes
  useEffect(() => {
    if (pendingAuction === null) return;
    if (pendingAuction.activeBidder === lastActiveBidderRef.current) return;
    lastActiveBidderRef.current = pendingAuction.activeBidder;
    setPrivacyVisible(true);
    setBidValue(0);
    const t = window.setTimeout(() => setPrivacyVisible(false), PRIVACY_BEAT_MS);
    return () => window.clearTimeout(t);
  }, [pendingAuction?.activeBidder, pendingAuction]);

  // Show privacy on fresh mount
  useEffect(() => {
    if (pendingAuction === null) {
      resolvedRef.current = false;
      setSecondsLeft(30);
      lastActiveBidderRef.current = -1;
      return;
    }
    if (lastActiveBidderRef.current === -1) {
      lastActiveBidderRef.current = pendingAuction.activeBidder;
      setPrivacyVisible(true);
      const t = window.setTimeout(() => setPrivacyVisible(false), PRIVACY_BEAT_MS);
      return () => window.clearTimeout(t);
    }
  }, [pendingAuction]);

  // Countdown timer tied to pendingAuction.deadline
  useEffect(() => {
    if (pendingAuction === null || pendingAuction.revealed) return;
    resolvedRef.current = false;
    const tick = window.setInterval(() => {
      const remaining = Math.max(0, Math.ceil((pendingAuction.deadline - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0 && !resolvedRef.current) {
        resolvedRef.current = true;
        window.clearInterval(tick);
        revealAuction();
      }
    }, 200);
    return () => window.clearInterval(tick);
  }, [pendingAuction, revealAuction]);

  // Auto-reveal when all 4 have bid (confirmed OR skipped = non-null)
  useEffect(() => {
    if (pendingAuction === null || pendingAuction.revealed) return;
    const allDone = pendingAuction.bids.every((b) => b !== null);
    if (allDone && !resolvedRef.current) {
      resolvedRef.current = true;
      revealAuction();
    }
  }, [pendingAuction, revealAuction]);

  // DD Bug#9c: auto-skip jailed activeBidder via useEffect.
  // When activeBidder is in jail and hasn't bid yet, fire skipAuctionBid + passAuctionTurn
  // automatically so the turn advances without manual interaction.
  useEffect(() => {
    if (pendingAuction === null || pendingAuction.revealed) return;
    if (privacyVisible) return; // wait for privacy beat to clear first
    const activeSeat = pendingAuction.activeBidder;
    const isJailed = (jailTurnsRemaining[activeSeat] ?? 0) > 0;
    if (!isJailed) return;
    if (pendingAuction.bids[activeSeat] !== null) return; // already handled
    // Short delay so UI shows the jail message before auto-skipping
    const timer = window.setTimeout(() => {
      skipAuctionBid();
      // Give store a tick to update bids before passAuctionTurn reads it.
      window.setTimeout(() => passAuctionTurn(), 50);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [pendingAuction, privacyVisible, jailTurnsRemaining, skipAuctionBid, passAuctionTurn]);

  // Hard gates
  if (pendingAuction === null || boardAnimationActive) return null;

  const { tier, cards, activeBidder, bids, revealed } = pendingAuction;

  // ── REVEAL PHASE ───────────────────────────────────────────────────────────
  if (revealed) {
    // Find winner: highest non-null bid; ties → activeBidder (drawer) wins
    // currentSeat was the drawer when auction was triggered; store it as drawerSeat
    const drawerSeat = pendingAuction.drawerSeat;
    let winnerSeat: SeatIndex | null = null;
    let winnerBid = -1;
    for (let i = 0; i < 4; i++) {
      if (i >= numPlayers) continue; // not-in-game seat (default 4 → none skipped)
      const bid = bids[i];
      if (bid == null) continue; // null or undefined — skipped
      const bidAmt = bid.amount;
      if (bidAmt > winnerBid) {
        winnerBid = bidAmt;
        winnerSeat = i as SeatIndex;
      } else if (bidAmt === winnerBid && winnerSeat !== null) {
        // Tie → drawer wins
        if (i === drawerSeat) winnerSeat = i as SeatIndex;
      }
    }
    const wonCard = cards[0]; // winner takes the first (auctioned) card
    const wonCardDef = wonCard ? getCardById(wonCard.id) : undefined;

    return (
      <div
        className={styles.overlay}
        // biome-ignore lint/a11y/useSemanticElements: <dialog> resets stacking context; custom overlay needed for z-index layering
        role="dialog"
        aria-modal="true"
        aria-label="Auction Result"
        data-testid="policy-auction-modal"
        data-phase="reveal"
      >
        <div className={styles.panel}>
          <div className={styles.title}>AUCTION RESULT</div>
          <div className={styles.revealSection}>
            <div className={styles.revealTitle}>BIDS REVEALED</div>

            {/* Auctioned card */}
            {wonCardDef !== undefined && (
              <div className={styles.revealCard}>
                <div className={styles.revealCardLabel}>AUCTIONED CARD</div>
                <div className={styles.revealCardName}>{wonCardDef.cardName}</div>
              </div>
            )}

            {/* Only in-game seats (< numPlayers) as participants. Default 4 → all four. */}
            <div className={styles.bidTable}>
              {(([0, 1, 2, 3] as SeatIndex[]).slice(0, numPlayers) as SeatIndex[]).map((seat) => {
                const bid = bids[seat];
                const isWinner = seat === winnerSeat;
                const isTieNote =
                  isWinner &&
                  bid !== null &&
                  winnerBid ===
                    bids.find((b, idx) => idx !== seat && b !== null && b.amount === winnerBid)
                      ?.amount;
                return (
                  <div
                    key={seat}
                    className={`${styles.bidRow} ${isWinner ? styles.bidRowWinner : ""}`}
                    data-testid={`auction-bid-row-${seat}`}
                  >
                    <span
                      className={styles.bidSeatLabel}
                      // biome-ignore lint/style/noNonNullAssertion: SEAT_COLORS always has 4 entries
                      style={{ color: SEAT_COLORS[seat]! }}
                    >
                      {seatLabel(seat)}
                    </span>
                    {/* Codex Phase 2b LOW 8 fix: skip sentinel renders as
                        "SKIPPED" not "-1T". null = didn't enter; -1 = explicit skip. */}
                    {bid === null || bid.amount < 0 ? (
                      <span className={styles.bidNull}>SKIPPED</span>
                    ) : (
                      <span className={styles.bidValue}>{bid.amount}T</span>
                    )}
                    {isWinner && <span className={styles.winnerBadge}>WINNER</span>}
                    {isTieNote && <span className={styles.tieNote}>(tie → drawer wins)</span>}
                  </div>
                );
              })}
            </div>

            {winnerSeat === null ? (
              <div className={styles.noWinnerNote}>
                All seats skipped — no winner. Card discarded.
              </div>
            ) : (
              <div className={styles.biddingPrompt}>
                {seatLabel(winnerSeat)} wins by paying {winnerBid}T.
              </div>
            )}

            <button
              type="button"
              className={styles.btnClose}
              onClick={closeAuction}
              data-testid="auction-close-btn"
            >
              CONTINUE
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── BIDDING PHASE ──────────────────────────────────────────────────────────
  // R13 Bug #14 root-cause fix: isActiveBidder was gated on currentSeat === activeBidder.
  // In hot-seat play currentSeat stays as the DRAWER for the whole turn, so only seat 0
  // ever saw bid controls. The store correctly uses activeBidder for all bid writes —
  // the UI must match. Since the device is passed physically, we always render controls
  // for whoever activeBidder currently is, regardless of currentSeat.
  const activeBidderTokens = tokens[activeBidder as SeatIndex] ?? 0;
  // DD Bug#9b: activeBidDone means the current activeBidder has already recorded a bid.
  // passAuctionTurn is now what advances activeBidder — so activeBidDone = true means
  // "bid is in; show the Pass Device button".
  const activeBidDone = bids[activeBidder as SeatIndex] !== null;
  // DD Bug#9c: is the current activeBidder jailed?
  const activeBidderJailed = (jailTurnsRemaining[activeBidder as SeatIndex] ?? 0) > 0;
  // Compute the next seat that still needs to bid (for Pass Device label)
  const nextNonBidSeat = (() => {
    for (let step = 1; step <= 4; step++) {
      const candidate = ((activeBidder + step) % 4) as SeatIndex;
      if (bids[candidate] === null) return candidate;
    }
    return null; // all done
  })();

  const circumference = 251.2;
  const progress = secondsLeft / 30;
  const strokeOffset = circumference * (1 - progress);

  return (
    <div
      className={styles.overlay}
      // biome-ignore lint/a11y/useSemanticElements: <dialog> resets stacking context; custom overlay needed for z-index layering
      role="dialog"
      aria-modal="true"
      aria-label="Policy Auction"
      data-testid="policy-auction-modal"
      data-phase="bidding"
    >
      <div className={styles.panel}>
        {/* Inner panel wrapper for relative positioning of privacy overlay */}
        <div className={styles.panelInner}>
          {/* Privacy beat — shown for 3s when activeBidder changes */}
          {privacyVisible && (
            <div className={styles.privacyOverlay} data-testid="auction-privacy-overlay">
              <div className={styles.privacyTitle}>AUCTION BEGINNING</div>
              <div className={styles.privacySubtitle}>
                {seatLabel(activeBidder as SeatIndex)} enters their bid first
              </div>
              <div className={styles.privacySubtitle} style={{ marginTop: 8 }}>
                Other players, please look away
              </div>
            </div>
          )}

          {/* Header: timer + title */}
          <div className={styles.header}>
            <svg
              width="64"
              height="64"
              viewBox="0 0 64 64"
              className={styles.timerRing}
              aria-hidden="true"
            >
              <circle className={styles.timerCircleBg} cx="32" cy="32" r="26" />
              {secondsLeft > 0 ? (
                <circle
                  className={styles.timerCircleFg}
                  cx="32"
                  cy="32"
                  r="26"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeOffset}
                />
              ) : (
                <circle className={styles.timerCircleExpired} cx="32" cy="32" r="26" />
              )}
              <text className={styles.timerText} x="32" y="32" textAnchor="middle">
                {secondsLeft}
              </text>
            </svg>

            <div className={styles.titleBlock}>
              <div className={styles.title}>POLICY AUCTION</div>
              <div className={styles.subtitle}>
                Tier {tier} policy card up for bid ·{" "}
                {(jailTurnsRemaining[activeBidder as SeatIndex] ?? 0) > 0
                  ? `${seatLabel(activeBidder as SeatIndex)} is in jail — auto-skipping`
                  : `${seatLabel(activeBidder as SeatIndex)} bidding now`}
              </div>
            </div>
          </div>

          {/* DD Bug#9a: 1 featured card (spec: all players bid on same single card) */}
          <div className={styles.cardSlate} data-testid="auction-card-slate">
            {(() => {
              const card = cards[0];
              if (card === undefined) return null;
              const def = getCardById(card.id);
              const typeCls =
                def?.cardType === "Policy"
                  ? styles.cardTypePolicy
                  : def?.cardType === "Mitigate"
                    ? styles.cardTypeMitigate
                    : styles.cardTypeTech;
              return (
                <div
                  key={card.instanceId}
                  className={`${styles.cardSlot} ${styles.cardSlotFeatured}`}
                  data-testid="auction-card-0"
                >
                  <div className={styles.cardTier}>TIER {tier}</div>
                  <div className={styles.cardName}>{def?.cardName ?? card.id}</div>
                  {def?.cardType !== undefined && (
                    <span className={`${styles.cardType} ${typeCls}`}>{def.cardType}</span>
                  )}
                  {def !== undefined && def.effects.length > 0 && (
                    <div className={styles.cardEffectsPreview}>
                      {def.effects.slice(0, 3).map((eff, ei) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: effect list is static per card def
                        <div key={ei} className={styles.cardEffectLine}>
                          {eff.kind}
                          {typeof (eff as unknown as { amount?: number }).amount === "number"
                            ? ` ×${(eff as unknown as { amount: number }).amount}`
                            : ""}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* R13 Bug #14: instruction banner — always visible during bidding */}
          <div className={styles.instructionBanner} data-testid="auction-instruction-banner">
            <span
              className={styles.instructionSeatChip}
              style={{
                background: `${SEAT_COLORS[activeBidder as SeatIndex] ?? "#888"}33`,
                borderColor: SEAT_COLORS[activeBidder as SeatIndex] ?? "#888",
              }}
            >
              {seatLabel(activeBidder as SeatIndex)}
            </span>
            <span className={styles.instructionText}>
              's turn to bid. Other players please look away. Enter your bid or skip.
            </span>
          </div>

          {/* Bidding section — always shows for the ACTIVE BIDDER regardless of currentSeat */}
          {activeBidderJailed && !activeBidDone ? (
            // DD Bug#9c: active bidder is jailed — show jail notice, auto-skip fires via useEffect
            <div className={styles.biddingSection} data-testid="auction-jail-section">
              <div className={styles.jailNotice} data-testid="auction-jail-notice">
                {seatLabel(activeBidder as SeatIndex)} is in jail — cannot bid. Auto-skipping…
              </div>
            </div>
          ) : !activeBidDone ? (
            // Active bidder sees slider + Confirm / Skip
            <div className={styles.biddingSection} data-testid="auction-bidder-section">
              <div className={styles.biddingHeader}>
                {seatLabel(activeBidder as SeatIndex)}'s Bid
              </div>
              <div className={styles.lookAwayNote}>{t("auction.lookAway")}</div>
              <div className={styles.balanceNote}>
                {seatLabel(activeBidder as SeatIndex)} balance: {activeBidderTokens}T
              </div>
              <div className={styles.sliderRow}>
                <input
                  type="range"
                  className={styles.bidSlider}
                  min={0}
                  max={activeBidderTokens}
                  value={bidValue}
                  onChange={(e) => setBidValue(Number(e.target.value))}
                  data-testid="auction-bid-slider"
                  aria-label="Bid amount"
                />
                <span className={styles.bidAmount}>{bidValue}T</span>
              </div>
              <div className={styles.bidActions}>
                <button
                  type="button"
                  className={styles.btnConfirm}
                  onClick={() => submitAuctionBid(bidValue)}
                  data-testid="auction-confirm-btn"
                >
                  {t("auction.confirmBid")}
                </button>
                <button
                  type="button"
                  className={styles.btnSkip}
                  onClick={skipAuctionBid}
                  data-testid="auction-skip-btn"
                >
                  SKIP
                </button>
              </div>
            </div>
          ) : (
            // DD Bug#9b: activeBidder has submitted/skipped — show Pass Device button.
            // passAuctionTurn (not submit/skip) now advances activeBidder.
            <div className={styles.biddingSection} data-testid="auction-pass-section">
              <div className={styles.waitingRow}>
                <span className={styles.waitingText}>
                  {seatLabel(activeBidder as SeatIndex)} bid confirmed.
                  {nextNonBidSeat !== null && (
                    <>
                      {" "}
                      {t("auction.passDevice", {
                        seat: seatLabel(nextNonBidSeat),
                      })}
                    </>
                  )}
                </span>
                <button
                  type="button"
                  className={styles.btnPass}
                  onClick={passAuctionTurn}
                  data-testid="auction-pass-btn"
                  disabled={nextNonBidSeat === null}
                >
                  {nextNonBidSeat !== null
                    ? `Pass Device to ${seatLabel(nextNonBidSeat)}`
                    : "All Bids Collected"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
